// src/pages/HardSkillsResults.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/HardSkillsResults.css";
import logoMirai from "../assets/mirai_logo.png";
import 'bootstrap-icons/font/bootstrap-icons.css';
import Navbar from '../components/Navbar';
import { PLATFORM_PDF_FOOTER } from '../constants/platform';

// React-PDF
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  PDFDownloadLink,
  Image,
} from "@react-pdf/renderer";

// === Estilos del PDF ===
const styles = StyleSheet.create({
  page: { padding: 40, fontSize: 12, fontFamily: "Helvetica" },
  header: { textAlign: "center", marginBottom: 20 },
  title: { fontSize: 18, fontWeight: "bold", color: "#004d40", marginTop: 10 },
  logo: { width: 60, height: 60, margin: "0 auto" },
  studentInfo: { marginBottom: 20 },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "bold",
    marginTop: 15,
    marginBottom: 8,
    color: "#333",
  },
  card: {
    border: "1pt solid #ccc",
    borderRadius: 6,
    padding: 8,
    marginBottom: 10,
  },
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  skillName: { fontSize: 12, fontWeight: "bold" },
  recommendation: { fontSize: 11, marginTop: 4, textAlign: "justify" },
  footer: {
    position: "absolute",
    bottom: 20,
    left: 40,
    right: 40,
    fontSize: 9,
    textAlign: "center",
    color: "#666",
  },
});

// === Documento PDF ===
const ResultsPDF = ({ user, results, recomendaciones, descriptions }) => {
  const date = new Date().toLocaleDateString();

  const getLevelColor = (level) => {
    switch (level) {
      case "Nivel muy bajo": return "#dc3545";
      case "Nivel bajo": return "#ff9800";
      case "Nivel medio": return "#ffc107";
      case "Nivel alto": return "#4caf50";
      case "Nivel muy alto": return "#28a745";
      default: return "#6c757d";
    }
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Marca de agua */}
        <Image
          src={logoMirai}
          style={{
            position: "absolute",
            top: "30%",
            left: "20%",
            width: 300,
            height: 300,
            opacity: 0.1,
          }}
        />

        {/* Header */}
        <View style={styles.header}>
          <Image src={logoMirai} style={styles.logo} />
          <Text style={styles.title}>Resultados de Inteligencias Múltiples</Text>
        </View>

        {/* Datos estudiante */}
        <View style={styles.studentInfo}>
          <Text>Nombre del estudiante: {user?.name || "N/D"}</Text>
          <Text>Fecha: {date}</Text>
        </View>

        {/* Detalle de inteligencias */}
        <Text style={styles.sectionTitle}>Detalle de Inteligencias:</Text>
        {Object.entries(results).map(([skill, data]) => (
          <View key={skill} style={styles.card} wrap={false}>
            <View style={styles.cardHeader}>
              <Text style={styles.skillName}>{skill}</Text>
              <Text style={{ color: getLevelColor(data.level), fontWeight: "bold" }}>
                {data.level}
              </Text>
            </View>
            {descriptions[skill] && (
              <Text style={styles.recommendation}>
                <Text style={{ fontWeight: "bold" }}>Descripción: </Text>
                {descriptions[skill]}
              </Text>
            )}
            {recomendaciones[skill]?.[data.level] && (
              <Text style={styles.recommendation}>
                <Text style={{ fontWeight: "bold" }}>Recomendación: </Text>
                {recomendaciones[skill][data.level]}
              </Text>
            )}
          </View>
        ))}

        {/* Footer */}
        <Text style={styles.footer}>
          {PLATFORM_PDF_FOOTER}
        </Text>
      </Page>
    </Document>
  );
};

const HardSkillsResults = () => {
  const [results, setResults] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/me`, {
          headers: { Authorization: token },
        });
        setResults(response.data.hardSkillsResults || {});
        setUser(response.data);
      } catch (error) {
        console.error("Error al obtener resultados:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchResults();
  }, []);

  // mismo getLevelColor que Soft Skills
  const getLevelColor = (level) => {
    switch (level) {
      case 'Nivel muy bajo': return '#dc3545';
      case 'Nivel bajo': return '#ff9800';
      case 'Nivel medio': return '#ffc107';
      case 'Nivel alto': return '#4caf50';
      case 'Nivel muy alto': return '#28a745';
      default: return '#6c757d';
    }
  };

  const intelligenceDescriptions = {
    'Lingüística': 'Capacidad para usar las palabras de manera efectiva, sea de manera oral o escrita. Incluye la habilidad en el uso de la sintaxis, la fonética, la semántica y los usos pragmáticos del lenguaje.',
    'Lógico-Matemática': 'Capacidad para usar los números de manera efectiva y de razonar adecuadamente. Incluye la sensibilidad a los esquemas y relaciones lógicas, las afirmaciones y las proposiciones.',
    'Espacial': 'Capacidad de pensar en tres dimensiones. Permite percibir imágenes externas e internas, recrearlas, transformarlas o modificarlas, recorrer el espacio o hacer que los objetos lo recorran.',
    'Musical': 'Capacidad de percibir, discriminar, transformar y expresar las formas musicales. Incluye la sensibilidad al ritmo, al tono y al timbre.',
    'Corporal-Kinestésica': 'Capacidad para usar todo el cuerpo en la expresión de ideas y sentimientos, y la facilidad en el uso de las manos para transformar elementos.',
    'Interpersonal': 'Capacidad de entender a los demás e interactuar eficazmente con ellos. Incluye la sensibilidad a expresiones faciales, la voz, los gestos y posturas.',
    'Intrapersonal': 'Capacidad de construir una percepción precisa respecto de sí mismo y de organizar y dirigir su propia vida.',
    'Naturalista': 'Capacidad de distinguir, clasificar y utilizar elementos del medio ambiente, objetos, animales o plantas.'
  };

  const recomendaciones = {
    "Inteligencia Comunicativa": {
      "Nivel muy bajo": "Tienes áreas de oportunidad importantes en tu expresión oral y escrita. Se recomienda practicar lectura diaria en voz alta, escribir pequeños ensayos o cuentos, y participar en juegos de roles o debates simulados. Considera tomar talleres de oratoria o redacción.",
      "Nivel bajo": "Tu habilidad comunicativa está en desarrollo. Participar en exposiciones, leer en voz alta y grabarte hablando sobre un tema puede ayudarte a ganar claridad y confianza. También puedes escribir en un diario para mejorar tu organización de ideas.",
      "Nivel medio": "Tienes una buena base comunicativa. Se sugiere reforzarla con presentaciones en clase, participación activa en discusiones grupales, y prácticas como el storytelling. Aprovecha esta habilidad en proyectos colaborativos.",
      "Nivel alto": "Te comunicas con eficacia en la mayoría de los contextos. Puedes usar tu habilidad para facilitar debates, redactar artículos escolares o incluso apoyar a tus compañeros como moderador o expositor.",
      "Nivel muy alto": "Tienes un gran dominio de la expresión verbal y escrita. Considera liderar actividades como periódicos escolares, concursos de oratoria o incluso iniciar un blog. Puedes influir positivamente en los demás con tu forma de comunicar.",
    },
    "Inteligencia Matemática": {
      "Nivel muy bajo": "Presentas dificultad con el razonamiento lógico y numérico. Se recomienda iniciar con juegos de lógica, rompecabezas, actividades manipulativas y ejercicios básicos de aritmética para fortalecer fundamentos.",
      "Nivel bajo": "Tu razonamiento está en desarrollo. Practica problemas de lógica cotidiana, desafíos de patrones o secuencias, y usa plataformas educativas para afianzar conceptos.",
      "Nivel medio": "Tienes habilidades numéricas aceptables. Refuerza esta competencia resolviendo acertijos lógicos, usando software de simulación matemática o programando pequeños retos.",
      "Nivel alto": " Demuestras pensamiento lógico avanzado. Utilízalo en actividades como programación, análisis de datos, matemáticas aplicadas o robótica educativa.",
      "Nivel muy alto": "Tienes una mente altamente analítica. Participa en competencias matemáticas, desarrolla algoritmos o investiga modelos matemáticos. Considera una orientación a ciencias o ingeniería.",
    },
    "Inteligencia Visual": {
      "Nivel muy bajo": "Te cuesta procesar información visual o espacial. Puedes mejorar con actividades como armar rompecabezas, juegos de construcción, dibujo libre y modelado en arcilla.",
      "Nivel bajo": "Tienes nociones básicas del espacio y forma. Apoya tu aprendizaje con mapas mentales, diagramas, o diseño de estructuras. Las apps de diseño 2D pueden ayudarte.",
      "Nivel medio": "Entiendes y procesas bien imágenes y estructuras. Usa herramientas visuales para estudiar o explicar conceptos. Participa en diseño gráfico o arquitectura básica.",
      "Nivel alto": "Tienes una buena capacidad espacial. Puedes usar software de diseño, herramientas CAD, edición de video o explorar fotografía creativa.",
      "Nivel muy alto": " Excelente percepción visual y espacial. Considera carreras como diseño, arquitectura, animación o artes visuales. Lidera proyectos creativos que requieran visión estética.",
    },
    "Inteligencia Motriz": {
      "Nivel muy bajo": "Muestras dificultad en la coordinación física. Participa en ejercicios suaves, yoga o danza básica para aumentar conciencia corporal. Trabaja la motricidad fina con manualidades.",
      "Nivel bajo": " Estás desarrollando tus habilidades corporales. Elige deportes suaves, actividades teatrales o juegos que impliquen movimientos. La práctica constante mejora el control.",
      "Nivel medio": "Muestras buen dominio corporal. Refuerza con actividades como deportes en equipo, danza o actuación. Explora tu creatividad física.",
      "Nivel alto": "Tu coordinación es sobresaliente. Participa en actividades físicas avanzadas como acrobacia, danza contemporánea, parkour o deportes de alto rendimiento.",
      "Nivel muy alto": "Talento corporal destacado. Considera disciplinas profesionales como la danza, el teatro físico, el circo, fisioterapia o educación física.",
    },
    "Inteligencia Rítmica": {
      "Nivel muy bajo": "Te cuesta seguir ritmos o percibir tonos. Escucha música variada, marca el ritmo con palmas y canta melodías simples. Puedes usar apps de entrenamiento musical.",
      "Nivel bajo": "Reconoces algunos ritmos y melodías. Practica con instrumentos simples como el teclado o percusión. Participa en juegos rítmicos grupales.",
      "Nivel medio": "Buen sentido del ritmo. Canta, toca un instrumento o graba sonidos. Participa en ensambles musicales o grupos escolares.",
      "Nivel alto": "Eres musicalmente sensible. Puedes crear composiciones sencillas, improvisar o interpretar con soltura. Únete a bandas o coros.",
      "Nivel muy alto": "Tienes oído musical destacado. Considera estudiar teoría musical, producción de audio o dirección coral. Puedes enseñar o incluso componer.",
    },
    "Inteligencia de Autoconocimiento": {
      "Nivel muy bajo": "Te cuesta identificar tus emociones o motivaciones. Usa diarios personales, meditación guiada o conversaciones con personas de confianza.",
      "Nivel bajo": "Estás en proceso de conocerte. Reflexiona sobre tus reacciones y escribe sobre tus metas personales. Practica la autorregulación.",
      "Nivel medio": "Tienes buen autoconocimiento. Profundiza definiendo valores, metas a largo plazo y tomando decisiones basadas en tus principios.",
      "Nivel alto": "Excelente introspección. Comparte tus aprendizajes con otros, guía grupos o toma el liderazgo en decisiones conscientes.",
      "Nivel muy alto": "Gran desarrollo personal. Puedes enseñar habilidades de autoconocimiento, acompañar procesos emocionales o desempeñarte en psicología, coaching o liderazgo.",
    },
    "Inteligencia Social": {
      "Nivel muy bajo": "Muestras dificultad para relacionarte con los demás. Participa en juegos colaborativos y busca espacios seguros para expresarte.",
      "Nivel bajo": "Puedes mejorar tus relaciones observando y escuchando activamente. Participa en actividades grupales estructuradas.",
      "Nivel medio": "Te relacionas bien. Refuerza tu empatía y habilidades de comunicación en equipo. Usa tus habilidades en proyectos colaborativos.",
      "Nivel alto": "Eres sociable y cooperativo. Apoya a tus compañeros, facilita diálogos o toma roles de coordinación grupal.",
      "Nivel muy alto": "Gran inteligencia social. Puedes formarte en liderazgo, mediación de conflictos o trabajo comunitario. Inspiras y conectas fácilmente con otros.",
    },
  };

  const renderContent = () => {
    if (loading) return <p>Cargando resultados...</p>;
    if (!results) return <p>No se encontraron resultados.</p>;

    switch (activeSection) {
      case 'details':
        return (
          <div className="results-section">
            <h3><i className="bi bi-list-check"></i> Tipos de Inteligencias</h3>
            <div className="intelligence-grid">
              {Object.entries(results).map(([skill, data]) => (
                <div key={skill} className="intelligence-card">
                  <div className="intelligence-header"><h4>{skill}</h4></div>
                  <p className="intelligence-description">{intelligenceDescriptions[skill]}</p>
                  <div className="level-bar">
                    <div
                      className="level-fill"
                      style={{ width: `${(data.level / 5) * 100}%`, backgroundColor: getLevelColor(data.level) }}
                    />
                  </div>
                  <div className="recommendations-section mt-4">
                    <p><strong>Recomendación:</strong> {recomendaciones[skill]?.[data.level] || "N/D"}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      default:
        return (
          <div className="results-content">
            <h3><i className="bi bi-graph-up"></i> Resumen de Inteligencias</h3>
            <div className="intelligence-chart">
              {Object.entries(results).map(([skill, data]) => (
                <div key={skill} className="chart-bar">
                  <span>{skill}</span>
                  <div className="bar-container">
                    <div className="bar-fill" style={{ width: `${(data.level / 5) * 100}%`, backgroundColor: getLevelColor(data.level) }}>
                      {data.level}
                    </div>
                  </div>
                </div>
              ))}

              <p style={{ textAlign: "center" }}><strong>Haz clic en el siguiente botón de detalles para más información</strong></p>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '10vh' }}>
                <button
                  className="custom-btn "
                  onClick={() => setActiveSection('details')}
                >
                  <i className="bi bi-info-circle"></i> Detalles
                </button>
              </div>

            </div>
            <div className="results-actions text-center mt-4">
              <div className="d-inline-block">
                <PDFDownloadLink
                  document={<ResultsPDF user={user} results={results} recomendaciones={recomendaciones} descriptions={intelligenceDescriptions} />}
                  fileName={`Resultados_InteligenciasMultiples_${new Date().toLocaleDateString().replaceAll("/", "_")}.pdf`}
                >
                  {({ loading }) =>
                    loading ? (
                      <button className="btn btn-secondary px-4 py-2">Generando PDF...</button>
                    ) : (
                      <button className="btn btn-primary px-4 py-2"><i className="bi bi-download"></i> Descargar Resultados</button>
                    )
                  }
                </PDFDownloadLink>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <>
      <Navbar />
      <div className="hard-skills-results-container">
        <div className="dashboard-layout">
          {/* Sidebar */}
          <div className="dashboard-sidebar">
            <div className="sidebar-header"><h3>Inteligencias Múltiples</h3></div>
            <div className="sidebar-menu">
              <button className={`menu-item ${activeSection === 'overview' ? 'active' : ''}`} onClick={() => setActiveSection('overview')}>
                <i className="bi bi-speedometer2"></i> Resumen
              </button>
              <button className={`menu-item ${activeSection === 'details' ? 'active' : ''}`} onClick={() => setActiveSection('details')}>
                <i className="bi bi-list-check"></i> Detalles
              </button>
              <button className="menu-item return-dashboard" onClick={() => navigate('/dashboard')}>
                <i className="bi bi-arrow-left-circle"></i> Volver al Dashboard
              </button>
            </div>
          </div>
          {/* Main Content */}
          <div className="dashboard-main">{renderContent()}</div>
        </div>
      </div>
    </>
  );
};

export default HardSkillsResults;
